import { useEffect, useImperativeHandle, useRef, useState, type ReactNode, type Ref } from 'react';

import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { TBody, THead, Table, Tr } from '@/shared/ui/Table';
import { ArrowRightIcon, PlusIcon, XIcon } from '@/shared/ui/icons';
import { tariffDate } from '@/shared/tariffs/types';
import { cn } from '@/shared/utils/cn';

import { basis, discounts, money, percent, unitPrice } from './format';
import {
  fromQuote,
  isHandPriced,
  isOverMaximum,
  isTariffLine,
  toPayload,
  type DraftLine,
} from './lineDrafts';
import type { Quote, QuoteLinesWrite } from './types';

/** What the page can ask the line editor to do: save what is on screen. */
export interface LineEditorHandle {
  save: () => Promise<boolean>;
}

let counter = 0;

function blankLine(): DraftLine {
  counter += 1;
  return {
    key: `nueva-${counter}`,
    keep_line_id: null,
    description: '',
    supplier_reference: '',
    unit: 'ud',
    quantity: '1',
    margin_pct: '',
    discount_pct: '0',
    tax_rate: '21',
    article_code: null,
    net_cost: null,
    supplier_unit_price: null,
    supplier_discounts: null,
    price_unit_basis: 1,
    // Empty, not '0': it is an input now, and the API reads empty as "no price yet".
    unit_price: '',
    line_total: '0',
    availability: null,
    edited_by_human: false,
    price_source: 'cost',
    tariff_supplier: null,
    tariff_date: null,
    max_discount_pct: null,
    effective_margin_pct: null,
  };
}

/** «Bosch · tarifa del 15/01/2026»: where the price of a line from the tariffs comes from,
 * dated as Documentos dates the tariff. Without the supplier in the counter mode. */
function tariffOrigin(line: DraftLine, showSupplier: boolean): string {
  const day = line.tariff_date ? tariffDate(line.tariff_date) : null;
  return [showSupplier ? line.tariff_supplier : null, day ? `tarifa del ${day}` : 'tarifa']
    .filter(Boolean)
    .join(' · ');
}

/**
 * What is wrong with these lines, in Spanish, or an empty list.
 *
 * Checked here rather than letting the API answer, because the API answers about
 * `lines[3].description` and the person is looking at a table. The rule itself is still
 * the API's: `description` has `minLength: 1` in the contract and this only says it
 * earlier and in front of the right row.
 */
function problemsOf(lines: DraftLine[]): string[] {
  return lines.flatMap((line, index) => {
    const where = `Línea ${index + 1}`;
    const problems: string[] = [];
    if (!line.description.trim()) problems.push(`${where}: falta la descripción.`);
    if (!line.quantity.trim()) problems.push(`${where}: falta la cantidad.`);
    return problems;
  });
}

/** How many steps «Deshacer» remembers. More than anybody undoes by hand. */
const HISTORY_LIMIT = 50;

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
}

/*
 * The table has to fit in the left column at 1280 px without scrolling sideways, and
 * that column is about 620 px wide once the sidebar, the page padding and the sticky
 * column with the customer picker have taken theirs. So every column but the
 * description has a small fixed width (about 500 px in all), the cells have 8 px of
 * padding, the inputs are 32 px high, and the supplier's tariff line goes under the
 * description, where there is room, rather than under the cost, where it would wrap
 * three times.
 *
 * Under `md` the same rows are painted as cards: the header row is hidden, each row is
 * a two-column grid with a border, and each cell paints its own label from
 * `data-label` through `::before`. One DOM, one state, one set of labels: two copies of
 * an input bound to the same line would be two things a screen reader announces.
 * The roles are explicit because a `tr` that is `display: grid` loses its implicit one.
 */
const HEAD =
  'border-line-2 text-ink-3 border-b px-2 py-1.5 text-left text-[11.5px] font-semibold tracking-[0.08em] whitespace-nowrap uppercase';
const CELL =
  'px-2 py-1.5 max-md:p-0 max-md:before:mb-0.5 max-md:before:block max-md:before:text-[11px] max-md:before:font-semibold max-md:before:tracking-[0.08em] max-md:before:uppercase max-md:before:text-ink-3 max-md:before:content-[attr(data-label)]';
/** A cell of plain text in a row that has inputs: aligned with the text inside them. */
const TEXT_CELL = `${CELL} md:pt-3`;
const ROW =
  'max-md:border-line max-md:mb-2 max-md:grid max-md:grid-cols-2 max-md:gap-x-3 max-md:gap-y-2 max-md:rounded-lg max-md:border max-md:p-3';
const INPUT = 'field h-8 w-full min-w-0 px-1.5 text-[13.5px]';
const TEXT = 'text-ink text-[13.5px] leading-5';
const NOTE = 'text-ink-3 text-[12px] leading-4';

function IconButton({
  label,
  icon,
  disabled,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="text-ink-3 hover:bg-paper-2 hover:text-ink focus-visible:outline-accent inline-flex size-6 items-center justify-center rounded-md transition-colors focus-visible:outline focus-visible:outline-2 disabled:cursor-not-allowed disabled:opacity-40 max-md:size-9"
    >
      {icon}
    </button>
  );
}

/**
 * The table of lines, editable.
 *
 * What is **read only** here is the cost: `net_cost`, the supplier's tariff price, its
 * discounts and its unit basis are what the supplier said, they were read once and they are
 * not retyped from a browser. What is editable is everything Víctor decides: the
 * description, the quantity, the margin, the line discount, and which lines are on the
 * quote at all (the customer did not ask for everything on the offer, contexto §4.5).
 *
 * The one exception is a line with no cost, added by hand: "Portes", an article that was
 * not on the offer. There is no supplier figure to derive a price from, so the price is
 * typed, the margin cell is empty (nothing to apply it to) and the cost cell says so.
 *
 * The prices and totals shown are the ones the API last computed. They do **not** update as
 * you type, on purpose: a total that moves while somebody edits, computed by a different
 * implementation than the one that will produce the PDF, is a total that will eventually
 * disagree with the document. Save, and the API answers with the real numbers.
 *
 * With `disabled` (an approved, sent or cancelled quote) the same lines are painted as a
 * plain table: an input that cannot be edited looks like a bug, not like a document.
 *
 * It owns its card, so «Añadir línea» and «Guardar líneas» can sit in the header next to
 * whatever the page hands in as `toolbar` (the global margin).
 */
export function LineEditor({
  quote,
  onSave,
  saving,
  error,
  disabled,
  dirty,
  onDirtyChange,
  saveRef,
  toolbar,
  actions,
  showPurchase = true,
  showMaxDiscount = false,
  showSupplier = true,
  className,
}: {
  quote: Quote;
  /**
   * The lines on screen, and the ones the editor knew from the server (contract v10.52):
   * with lines also arriving from the search of the tariffs, a save that did not know one
   * would delete it, and the API answers 409 instead.
   */
  onSave: (payload: QuoteLinesWrite) => Promise<unknown>;
  saving: boolean;
  error: unknown;
  disabled: boolean;
  dirty: boolean;
  onDirtyChange: (dirty: boolean) => void;
  /**
   * The same save as «Guardar líneas», for the page: «Guardar y descargar borrador»
   * saves the lines on screen before asking for the PDF (contract v10.18). Resolves to
   * whether they were saved.
   */
  saveRef?: Ref<LineEditorHandle>;
  /** A row of controls under the header: the global margin, from the page. */
  toolbar?: ReactNode;
  /** More buttons in the header, before «Añadir línea»: «Pegar lista» (contract v10.52). */
  actions?: ReactNode;
  /**
   * The cost, the margin and the supplier's tariff under each description (contract
   * v10.47): off while the counter mode hides what Cubas pays from a customer looking at
   * the screen. The lines keep their margins; they are only not drawn.
   */
  showPurchase?: boolean;
  /** The supplier's maximum discount on the lines from the tariffs, and the mark of one
   * past it (contract v10.52): off for a profile without it and in the counter mode. */
  showMaxDiscount?: boolean;
  /** Whose tariff a line comes from (contract v10.52): off in the counter mode, which
   * says nothing of the suppliers on this screen. */
  showSupplier?: boolean;
  className?: string;
}) {
  const [lines, setLines] = useState<DraftLine[]>(() => (quote.lines ?? []).map(fromQuote));
  const [problems, setProblems] = useState<string[]>([]);
  const setDirty = onDirtyChange;
  /**
   * «Deshacer» (contract v10.24, asked for in the demo of 2026-09-24): the lines as they
   * were before each change, newest last. One entry per discrete action (remove, add,
   * move) and one per stretch of typing in a field, not one per key: undoing «Tornillo
   * M8x40» a letter at a time is not what anybody means by undo.
   *
   * Only what is on screen and unsaved. Once saved, the list is empty: a line removed and
   * saved cannot come back from here with its supplier cost, because the payload never
   * carries a cost and its `keep_line_id` no longer exists. «Rehacer» the quote is the way
   * back from a save.
   */
  const [history, setHistory] = useState<DraftLine[][]>([]);
  /** The field being typed in, `line:field`, while the typing lasts. */
  const typingIn = useRef<string | null>(null);
  /** The line just removed, for the «Línea 3 quitada. Deshacer» note. */
  const [removed, setRemoved] = useState<{ number: number; description: string } | null>(null);

  /**
   * The lines of the server this editor has seen, by id (contract v10.52). Lines now also
   * arrive from outside the table («Añadir» in the search of the tariffs, «Pegar lista»),
   * and a save is a full replacement: a line the editor never saw would be deleted by it.
   * The save sends these, and the API refuses with 409 when the quote has one more.
   */
  const known = useRef<Set<string>>(new Set((quote.lines ?? []).map((line) => line.id)));

  // The API answers every write with the whole quote, recomputed. Adopting it is what puts
  // the real prices on screen after a save; `dirty` keeps it from throwing away edits in
  // progress if something else refreshes the quote underneath. Adopting is also where the
  // history ends: what is on screen is what is saved, and there is nothing to undo.
  //
  // With edits in progress, what arrived from elsewhere joins the end of the table, and of
  // every step «Deshacer» can go back to: undoing past it must not drop it unseen.
  //
  // A quote that stops being editable (approved or cancelled from another tab) shows what
  // the server has: edits that can no longer be saved are not the document.
  useEffect(() => {
    const server = quote.lines ?? [];
    if (!dirty || disabled) {
      setLines(server.map(fromQuote));
      setHistory([]);
      typingIn.current = null;
      known.current = new Set(server.map((line) => line.id));
      if (dirty) setDirty(false);
      return;
    }
    const arrived = server.filter((line) => !known.current.has(line.id)).map(fromQuote);
    if (arrived.length === 0) return;
    for (const line of arrived) known.current.add(line.key);
    setLines((current) => [...current, ...arrived]);
    setHistory((past) => past.map((step) => [...step, ...arrived]));
  }, [quote, dirty, disabled, setDirty]);

  /** Keep `current` as the state «Deshacer» goes back to. */
  function remember(current: DraftLine[]): void {
    setHistory((past) => [...past.slice(-(HISTORY_LIMIT - 1)), current]);
  }

  function update(key: string, patch: Partial<DraftLine>): void {
    const field = `${key}:${Object.keys(patch).join(',')}`;
    if (typingIn.current !== field) {
      remember(lines);
      typingIn.current = field;
    }
    setRemoved(null);
    setDirty(true);
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  }

  function remove(key: string): void {
    const index = lines.findIndex((line) => line.key === key);
    remember(lines);
    typingIn.current = null;
    setRemoved(
      index >= 0 ? { number: index + 1, description: lines[index]?.description ?? '' } : null,
    );
    setDirty(true);
    setLines((current) => current.filter((line) => line.key !== key));
  }

  function add(): void {
    remember(lines);
    typingIn.current = null;
    setRemoved(null);
    setDirty(true);
    setLines((current) => [...current, blankLine()]);
  }

  function undo(): void {
    const previous = history[history.length - 1];
    if (!previous) return;
    setHistory((past) => past.slice(0, -1));
    setLines(previous);
    typingIn.current = null;
    setRemoved(null);
    setProblems([]);
    // Every change remembered the state before it, and the history starts empty at the
    // last save: undoing the last remembered change is being back at the save.
    setDirty(history.length > 1);
  }

  function discard(): void {
    setLines((quote.lines ?? []).map(fromQuote));
    known.current = new Set((quote.lines ?? []).map((line) => line.id));
    setHistory([]);
    typingIn.current = null;
    setRemoved(null);
    setProblems([]);
    setDirty(false);
  }

  // Ctrl+Z (Cmd+Z) undoes a line change when the focus is not in a field: inside one,
  // the browser's own undo of the text is what the person expects. Read through a ref so
  // the listener, registered once, always undoes the history of this render.
  const undoRef = useRef(undo);
  undoRef.current = undo;
  const canUndo = history.length > 0;
  useEffect(() => {
    if (disabled) return undefined;
    function onKey(event: KeyboardEvent): void {
      if (!(event.ctrlKey || event.metaKey) || event.shiftKey || event.altKey) return;
      if (event.key.toLowerCase() !== 'z' || isTyping(event.target)) return;
      event.preventDefault();
      undoRef.current();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [disabled]);

  // Unsaved lines are lost by leaving: closing or reloading the tab asks first, and so
  // does following a link of the app (the sidebar, «Presupuestos», «Ver el correo»). The
  // router here is not a data router, so `useBlocker` is not available; a link is an
  // `<a>`, and React Router does not navigate a click whose default was prevented.
  useEffect(() => {
    if (!dirty || disabled) return undefined;
    function onBeforeUnload(event: BeforeUnloadEvent): void {
      event.preventDefault();
      event.returnValue = '';
    }
    function onClick(event: MouseEvent): void {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!(link instanceof HTMLAnchorElement) || link.target === '_blank') return;
      if (link.origin !== window.location.origin) return;
      if (!window.confirm('Hay líneas sin guardar. ¿Salir sin guardarlas?')) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty, disabled]);

  function move(index: number, delta: number): void {
    remember(lines);
    typingIn.current = null;
    setRemoved(null);
    setDirty(true);
    // Bounds checked inside the updater, against `current` and not against the `lines` of
    // the render this closure was built in: two clicks batched into one render would
    // otherwise both measure the same stale length.
    setLines((current) => {
      const target = index + delta;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(index, 1);
      if (moved) next.splice(target, 0, moved);
      return next;
    });
  }

  /**
   * Save, and only clear the dirty flag **when the API says yes**.
   *
   * Clearing it on the click looked the same in the happy path and lost the work in the
   * only path that matters: a 409 ("somebody approved this in another tab") or a 422 left
   * the table showing the old figures, because the effect above adopts `quote` the moment
   * `dirty` goes false, and left the button disabled, because it is disabled when not
   * dirty. The edit was gone and there was no way to retry it.
   */
  function save(): Promise<boolean> {
    const found = problemsOf(lines);
    setProblems(found);
    if (found.length > 0) return Promise.resolve(false);
    return onSave({ lines: toPayload(lines), known_line_ids: [...known.current] }).then(
      () => {
        setDirty(false);
        setHistory([]);
        setRemoved(null);
        return true;
      },
      // The message is already on screen through `error`. Swallowed here only so the
      // rejection is handled; `dirty` deliberately stays true so the retry is possible.
      () => false,
    );
  }

  // No dependency list on purpose: `save` closes over the lines of this render, and the
  // handle has to hand out the lines on screen, not the ones of the first render.
  useImperativeHandle(saveRef, () => ({ save }));

  const editable = !disabled;

  return (
    <Card
      title={`Líneas (${lines.length})`}
      padded={false}
      {...(className ? { className } : {})}
      bodyClassName="pb-1"
      aside={
        editable ? (
          <>
            {actions}
            <Button variant="secondary" size="sm" icon={<PlusIcon size={15} />} onClick={add}>
              Añadir línea
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={undo}
              disabled={saving || !canUndo}
              title="Deshacer el último cambio de las líneas (Ctrl+Z)"
            >
              Deshacer
            </Button>
            <Button size="sm" onClick={() => void save()} disabled={saving || !dirty}>
              {saving ? 'Guardando…' : 'Guardar líneas'}
            </Button>
          </>
        ) : undefined
      }
    >
      {toolbar}

      {removed ? (
        <Callout compact tone="neutral" role="status" className="mx-3">
          Línea {removed.number} quitada
          {removed.description ? ` («${removed.description}»)` : ''}.{' '}
          <button type="button" className="link" onClick={undo}>
            Deshacer
          </button>
        </Callout>
      ) : null}

      {dirty ? (
        <Callout compact tone="warn" role="status" className="mx-3">
          Cambios sin guardar: los precios y los totales se actualizan al guardar.{' '}
          <button type="button" className="link" onClick={discard}>
            Descartar cambios
          </button>
        </Callout>
      ) : null}

      {problems.length > 0 ? (
        <ul
          role="alert"
          className="bg-danger-soft text-danger mx-3 flex flex-col gap-0.5 rounded-md px-3 py-2 text-[13.5px]"
        >
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      ) : null}

      {error ? (
        <ErrorMessage error={error} className="mx-3">
          No se pudieron guardar las líneas
        </ErrorMessage>
      ) : null}

      {lines.length === 0 ? (
        <p className="text-ink-3 px-4 py-6 text-center text-[13.5px]">
          {editable
            ? quote.origin === 'tariff'
              ? 'Sin líneas todavía. Pega la lista del cliente, añade artículos desde Documentos o escribe una con «Añadir línea».'
              : 'Sin líneas todavía. Añade una con «Añadir línea», o sube la oferta del proveedor.'
            : 'Este presupuesto no tiene líneas.'}
        </p>
      ) : (
        <Table label="Líneas" minWidth="0" className="max-md:px-3 max-md:pb-2">
          <THead>
            <tr className="max-md:hidden">
              <th scope="col" className={cn(HEAD, 'w-8 pr-0 pl-3')}>
                #
              </th>
              <th scope="col" className={HEAD}>
                Descripción
              </th>
              <th scope="col" className={cn(HEAD, 'w-14 text-right')}>
                Cant.
              </th>
              {showPurchase ? (
                <>
                  <th scope="col" className={cn(HEAD, 'w-18 text-right')}>
                    Coste
                  </th>
                  <th scope="col" className={cn(HEAD, 'w-13 text-right')}>
                    Margen
                  </th>
                </>
              ) : null}
              <th scope="col" className={cn(HEAD, 'w-13 text-right')}>
                Dto.
              </th>
              <th scope="col" className={cn(HEAD, 'w-18 text-right')}>
                Precio
              </th>
              <th scope="col" className={cn(HEAD, 'w-20 text-right')}>
                Total
              </th>
              {editable ? <th scope="col" className={cn(HEAD, 'w-20')} /> : null}
            </tr>
          </THead>
          <TBody>
            {lines.map((line, index) => (
              <LineRow
                key={line.key}
                line={line}
                index={index}
                count={lines.length}
                editable={editable}
                showPurchase={showPurchase}
                showMaxDiscount={showMaxDiscount}
                showSupplier={showSupplier}
                defaultMargin={quote.default_margin_pct ?? undefined}
                onChange={(patch) => update(line.key, patch)}
                onEditEnd={() => {
                  typingIn.current = null;
                }}
                onMove={(delta) => move(index, delta)}
                onRemove={() => remove(line.key)}
              />
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}

/** One line: inputs on a draft, text once the quote is closed. Same columns either way. */
function LineRow({
  line,
  index,
  count,
  editable,
  showPurchase,
  showMaxDiscount,
  showSupplier,
  defaultMargin,
  onChange,
  onEditEnd,
  onMove,
  onRemove,
}: {
  line: DraftLine;
  index: number;
  count: number;
  editable: boolean;
  showPurchase: boolean;
  showMaxDiscount: boolean;
  showSupplier: boolean;
  defaultMargin: string | undefined;
  onChange: (patch: Partial<DraftLine>) => void;
  /** Typing in a field ended (blur): the next change there is a new «Deshacer» step. */
  onEditEnd: () => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
}) {
  const number = index + 1;
  const textCell = editable ? TEXT_CELL : CELL;
  // Contract v10.52: the price of a line from the tariffs is the tariff's, and what moves
  // is the discount. The margin it leaves is the API's answer, read only.
  const fromTariff = isTariffLine(line);
  const overMaximum = showMaxDiscount && isOverMaximum(line);
  // Where the cost comes from, under the description: "why does this cost 0,45" has to
  // be answerable from the screen, and the cost column is too narrow to say it.
  const tariff =
    showPurchase && line.supplier_unit_price
      ? `Tarifa ${unitPrice(line.supplier_unit_price)}${basis(line.price_unit_basis)}${
          line.supplier_discounts?.length ? ` · ${discounts(line.supplier_discounts)}` : ''
        }`
      : null;

  return (
    <Tr role="row" className={ROW}>
      <td
        role="cell"
        className={cn(
          'text-ink-4 pr-0 pl-3 text-[12.5px] max-md:col-span-2 max-md:p-0',
          editable ? 'py-1.5 md:pt-3' : 'py-1.5',
          'max-md:text-ink-2 max-md:text-[13px] max-md:font-semibold',
        )}
      >
        <span className="md:hidden">Línea </span>
        {number}
      </td>
      <td role="cell" data-label="Descripción" className={cn(CELL, 'max-md:col-span-2')}>
        {editable ? (
          <input
            aria-label={`Descripción de la línea ${number}`}
            className={INPUT}
            value={line.description}
            onBlur={onEditEnd}
            onChange={(event) => onChange({ description: event.target.value })}
          />
        ) : (
          <span className={TEXT}>{line.description}</span>
        )}
        {fromTariff ? (
          <p className={cn(NOTE, 'mt-1')}>
            {tariffOrigin(line, showSupplier)}
            {showMaxDiscount && line.max_discount_pct
              ? ` · dto. máx. ${percent(line.max_discount_pct)}`
              : ''}
          </p>
        ) : null}
        {tariff ? <p className={cn(NOTE, 'mt-1')}>{tariff}</p> : null}
        {overMaximum ? (
          <p className="text-danger mt-0.5 text-[12px] leading-4 font-medium">
            Por encima del descuento máximo ({percent(line.max_discount_pct)})
          </p>
        ) : null}
        {line.availability ? <p className={cn(NOTE, 'mt-0.5')}>{line.availability}</p> : null}
        {line.edited_by_human ? (
          <p className="text-warn mt-0.5 text-[12px] leading-4">Editada a mano</p>
        ) : null}
      </td>
      <td role="cell" data-label="Cantidad" className={cn(CELL, 'num')}>
        {editable ? (
          <input
            aria-label={`Cantidad de la línea ${number}`}
            className={`${INPUT} num`}
            inputMode="decimal"
            value={line.quantity}
            onBlur={onEditEnd}
            onChange={(event) => onChange({ quantity: event.target.value })}
          />
        ) : (
          <span className={TEXT}>{line.quantity}</span>
        )}
      </td>
      {showPurchase ? (
        <>
          {/* Read only, and this is the point of the column: the cost is what the supplier
              said. A hand-added line has no supplier, so it says so. */}
          <td role="cell" data-label="Coste" className={cn(textCell, 'num')}>
            {isHandPriced(line) ? (
              <span className={cn(NOTE, 'inline-block max-md:text-left')}>
                {editable ? 'Sin proveedor: el precio lo pones tú' : 'precio a mano'}
              </span>
            ) : (
              <span className={cn(TEXT, 'tabular-nums')}>{unitPrice(line.net_cost)}</span>
            )}
          </td>
          <td role="cell" data-label="Margen %" className={cn(CELL, 'num')}>
            {/* A margin is applied to a cost. With no cost there is nothing to apply it to,
                and an input that changes nothing is a lie. */}
            {fromTariff ? (
              <span
                className={cn(TEXT, 'text-ink-2 inline-block', editable && 'md:pt-1.5')}
                title="Lo que deja el descuento sobre el coste. Cambia con el descuento, al guardar."
              >
                {line.effective_margin_pct ? percent(line.effective_margin_pct) : '—'}
              </span>
            ) : isHandPriced(line) ? (
              <span className={cn(TEXT, 'text-ink-4 inline-block', editable && 'md:pt-1.5')}>
                —
              </span>
            ) : editable ? (
              <input
                aria-label={`Margen de la línea ${number}`}
                className={`${INPUT} num`}
                inputMode="decimal"
                placeholder={defaultMargin}
                value={line.margin_pct}
                onBlur={onEditEnd}
                onChange={(event) => onChange({ margin_pct: event.target.value })}
              />
            ) : (
              <span className={cn(TEXT, 'text-ink-2')}>
                {line.margin_pct ? `${line.margin_pct} %` : 'por defecto'}
              </span>
            )}
          </td>
        </>
      ) : null}
      <td role="cell" data-label="Dto. %" className={cn(CELL, 'num')}>
        {editable ? (
          <input
            aria-label={`Descuento de la línea ${number}`}
            aria-invalid={overMaximum || undefined}
            className={cn(INPUT, 'num', overMaximum && 'border-danger text-danger')}
            inputMode="decimal"
            value={line.discount_pct}
            onBlur={onEditEnd}
            onChange={(event) => onChange({ discount_pct: event.target.value })}
          />
        ) : (
          <span className={cn(TEXT, overMaximum ? 'text-danger' : 'text-ink-2')}>
            {line.discount_pct} %
          </span>
        )}
      </td>
      <td role="cell" data-label="Precio" className={cn(CELL, 'num')}>
        {editable && isHandPriced(line) ? (
          <input
            aria-label={`Precio de la línea ${number}`}
            className={`${INPUT} num`}
            inputMode="decimal"
            placeholder="0,00"
            value={line.unit_price}
            onBlur={onEditEnd}
            onChange={(event) => onChange({ unit_price: event.target.value })}
          />
        ) : (
          <span className={cn(TEXT, 'inline-block whitespace-nowrap', editable && 'md:pt-1.5')}>
            {unitPrice(line.unit_price)}
          </span>
        )}
      </td>
      <td role="cell" data-label="Total" className={cn(textCell, 'num')}>
        <span className={cn(TEXT, 'font-medium whitespace-nowrap')}>{money(line.line_total)}</span>
      </td>
      {editable ? (
        <td role="cell" className="px-1 py-1.5 max-md:col-span-2 max-md:p-0">
          <div className="flex items-center gap-0.5 max-md:justify-end">
            <IconButton
              label={`Subir la línea ${number}`}
              icon={<ArrowRightIcon size={15} className="-rotate-90" />}
              disabled={index === 0}
              onClick={() => onMove(-1)}
            />
            <IconButton
              label={`Bajar la línea ${number}`}
              icon={<ArrowRightIcon size={15} className="rotate-90" />}
              disabled={index === count - 1}
              onClick={() => onMove(1)}
            />
            <IconButton
              label={`Quitar la línea ${number}`}
              icon={<XIcon size={15} />}
              onClick={onRemove}
            />
          </div>
        </td>
      ) : null}
    </Tr>
  );
}
